/* Clone theme behaviour: drawers, country selector, AJAX cart, carousels, reveals, reels viewer. */
(() => {
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

  /* Overlays (menu, cart, reels viewer) ---------------------------------------- */

  const openOverlays = new Set();
  const lockScroll = () => {
    document.documentElement.style.overflow = openOverlays.size ? "hidden" : "";
  };
  const showOverlay = (element) => {
    element.hidden = false;
    openOverlays.add(element);
    lockScroll();
  };
  const hideOverlay = (element) => {
    element.hidden = true;
    openOverlays.delete(element);
    lockScroll();
  };

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    // An open country list closes first (handled by its own listener).
    if ($$("[data-dd-country-list]").some((list) => !list.hidden)) return;
    const last = [...openOverlays].pop();
    if (last) last.dispatchEvent(new CustomEvent("dd:close"));
  });

  /* Mobile menu ------------------------------------------------------------- */

  function initMenu(root = document) {
    const menu = $("[data-dd-menu]", root);
    if (!menu || menu.dataset.ddReady) return;
    menu.dataset.ddReady = "1";
    const toggles = $$("[data-dd-menu-open]", root);
    const close = () => {
      hideOverlay(menu);
      toggles.forEach((button) => button.setAttribute("aria-expanded", "false"));
    };
    toggles.forEach((button) =>
      button.addEventListener("click", () => {
        showOverlay(menu);
        button.setAttribute("aria-expanded", "true");
      }),
    );
    $$("[data-dd-menu-close]", menu).forEach((button) => button.addEventListener("click", close));
    menu.addEventListener("dd:close", close);
  }

  /* Collection filter drawer (below 1000px) --------------------------------------- */

  function initFilters(root = document) {
    const layer = $("[data-dd-filter]", root);
    if (!layer || layer.dataset.ddReady) return;
    layer.dataset.ddReady = "1";
    const toggles = $$("[data-dd-filter-open]", root);
    const close = () => {
      hideOverlay(layer);
      toggles.forEach((button) => button.setAttribute("aria-expanded", "false"));
    };
    toggles.forEach((button) =>
      button.addEventListener("click", () => {
        showOverlay(layer);
        button.setAttribute("aria-expanded", "true");
      }),
    );
    $$("[data-dd-filter-close]", layer).forEach((button) => button.addEventListener("click", close));
    layer.addEventListener("dd:close", close);
  }

  /* Price filter: two range handles kept in step with the two number fields (drawer and sidebar). */
  function initPriceRanges(root = document) {
    $$("[data-dd-price]", root).forEach((group) => {
      const range = $("[data-dd-range]", group);
      if (!range || group.dataset.ddReady) return;
      group.dataset.ddReady = "1";
      const [lo, hi] = [$("[data-dd-range-min]", range), $("[data-dd-range-max]", range)];
      const [loField, hiField] = [$("[data-dd-range-min-input]", group), $("[data-dd-range-max-input]", group)];
      const paint = () => {
        const max = Number(hi.max) || 1;
        range.style.setProperty("--dd-range-from", `${(Number(lo.value) / max) * 100}%`);
        range.style.setProperty("--dd-range-to", `${(Number(hi.value) / max) * 100}%`);
      };
      lo.addEventListener("input", () => {
        if (Number(lo.value) > Number(hi.value)) lo.value = hi.value;
        loField.value = Number(lo.value) > 0 ? lo.value : "";
        paint();
      });
      hi.addEventListener("input", () => {
        if (Number(hi.value) < Number(lo.value)) hi.value = lo.value;
        hiField.value = Number(hi.value) < Number(hi.max) ? hi.value : "";
        paint();
      });
      loField.addEventListener("input", () => { lo.value = loField.value || 0; paint(); });
      hiField.addEventListener("input", () => { hi.value = hiField.value || hi.max; paint(); });
      paint();
    });
  }

  /* Amazon reviews (native rebuild of the Reputon widget) ------------------------- */

  function initAmazonReviews(root = document) {
    $$("[data-dd-amazon]", root).forEach((section) => {
      if (section.dataset.ddReady) return;
      section.dataset.ddReady = "1";
      const cards = $$("[data-dd-amazon-card]", section);
      const load = $("[data-dd-amazon-load]", section);
      const step = window.matchMedia("(max-width: 767px)").matches ? 2 : 6;
      let shown = 0;
      const reveal = () => {
        shown = Math.min(cards.length, shown + step);
        cards.forEach((card, index) => (card.hidden = index >= shown));
        load.hidden = shown >= cards.length;
        cards.slice(0, shown).forEach((card) => {
          const text = $("[data-dd-amazon-text]", card);
          if (!card.classList.contains("is-open")) $("[data-dd-amazon-more]", card).hidden = text.scrollHeight <= text.clientHeight + 1;
        });
      };
      reveal();
      load.addEventListener("click", reveal);
      cards.forEach((card) => {
        const more = $("[data-dd-amazon-more]", card);
        more.addEventListener("click", () => {
          const open = card.classList.toggle("is-open");
          more.textContent = open ? "Show less" : "Show more";
        });
      });
    });
  }

  /* Country selector ---------------------------------------------------------- */

  function initCountrySelectors(root = document) {
    $$("[data-dd-country]", root).forEach((wrapper) => {
      if (wrapper.dataset.ddReady) return;
      wrapper.dataset.ddReady = "1";
      const toggle = $("[data-dd-country-toggle]", wrapper);
      const list = $("[data-dd-country-list]", wrapper);
      const backdrop = $("[data-dd-country-close]", wrapper);
      const setOpen = (open) => {
        list.hidden = !open;
        backdrop.hidden = !open;
        toggle.setAttribute("aria-expanded", String(open));
      };
      toggle.addEventListener("click", () => setOpen(list.hidden));
      backdrop.addEventListener("click", () => setOpen(false));
      document.addEventListener("pointerdown", (event) => {
        if (!list.hidden && !wrapper.contains(event.target)) setOpen(false);
      });
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !list.hidden) {
          event.stopImmediatePropagation();
          setOpen(false);
        }
      });
    });
  }

  /* AJAX cart ------------------------------------------------------------------ */

  const cartRoot = $("[data-dd-cart]");
  const cartBody = cartRoot && $("[data-dd-cart-body]", cartRoot);
  const routesRoot = window.Shopify?.routes?.root ?? "/";

  const formatMoney = (cents, currency) =>
    new Intl.NumberFormat(cartRoot?.dataset.locale || document.documentElement.lang || "en", {
      style: "currency",
      currency,
    }).format(cents / 100);

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);

  const sizedImage = (src, width) => {
    const url = new URL(src, window.location.origin);
    url.searchParams.set("width", String(width));
    return url.toString();
  };

  function renderCart(cart) {
    if (!cartRoot) return;
    $$("[data-dd-cart-count]").forEach((bubble) => {
      bubble.textContent = cart.item_count;
      bubble.hidden = cart.item_count === 0;
    });
    $("[data-dd-cart-heading-count]", cartRoot).textContent = cart.item_count;

    if (cart.item_count === 0) {
      cartBody.innerHTML = `
        <div class="ddc-cartEmpty">
          <h3 class="ddc-heading">Your cart is empty.</h3>
          <a href="${escapeHtml(cartRoot.dataset.emptyUrl || "/collections/all")}" class="ddc-button">${escapeHtml(cartRoot.dataset.emptyLabel || "Continue shopping")}</a>
        </div>`;
    } else {
      cartBody.innerHTML = `<ul class="ddc-cartItems">${cart.items
        .map(
          (item) => `
          <li class="ddc-cartItem">
            <a href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(sizedImage(item.image, 160))}" alt="${escapeHtml(item.product_title)}" width="80" height="80" loading="lazy">` : ""}</a>
            <div>
              <a href="${escapeHtml(item.url)}" class="ddc-cartItemTitle">${escapeHtml(item.product_title)}</a>
              ${item.product_has_only_default_variant ? "" : `<p class="ddc-cartItemVariant">${escapeHtml(item.variant_title)}</p>`}
              <p class="ddc-cartItemPrice">
                <span class="ddc-salePrice">${formatMoney(item.final_price, cart.currency)}</span>
                ${item.original_price > item.final_price ? `<span class="ddc-comparePrice">${formatMoney(item.original_price, cart.currency)}</span>` : ""}
              </p>
              ${item.properties?._gift_for ? "" : `<div class="ddc-cartQty">
                <button type="button" aria-label="Decrease quantity" data-dd-qty="${escapeHtml(item.key)}" data-qty="${item.quantity - 1}">−</button>
                <span>${item.quantity}</span>
                <button type="button" aria-label="Increase quantity" data-dd-qty="${escapeHtml(item.key)}" data-qty="${item.quantity + 1}">+</button>
              </div>`}
              <button type="button" class="ddc-cartRemove" data-dd-qty="${escapeHtml(item.key)}" data-qty="0">Remove</button>
            </div>
          </li>`,
        )
        .join("")}</ul>`;
    }

    const threshold = Number(cartRoot.dataset.freeShipping || 0);
    const remaining = Math.max(0, threshold - cart.total_price);
    const text = $("[data-dd-shipping-text]", cartRoot);
    text.innerHTML =
      threshold === 0
        ? ""
        : remaining > 0
          ? `Spend <strong>${formatMoney(remaining, cart.currency)}</strong> more for free shipping`
          : "You are eligible for free shipping!";
    $("[data-dd-shipping-bar]", cartRoot).style.setProperty(
      "--progress",
      threshold ? Math.min(1, cart.total_price / threshold).toFixed(4) : "0",
    );
    $("[data-dd-cart-checkout]", cartRoot).hidden = cart.item_count === 0;
    $("[data-dd-cart-subtotal]", cartRoot).textContent = formatMoney(cart.total_price, cart.currency);
  }

  const fetchCart = () => fetch(`${routesRoot}cart.js`, { headers: { Accept: "application/json" } }).then((res) => res.json());

  async function openCart() {
    if (!cartRoot) {
      window.location.href = `${routesRoot}cart`;
      return;
    }
    renderCart(await dropOrphanGifts(await fetchCart()));
    showOverlay(cartRoot);
  }

  async function changeQuantity(key, quantity) {
    const res = await fetch(`${routesRoot}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });
    renderCart(await dropOrphanGifts(await res.json()));
  }

  // A gift stays only while its tier is in the cart: below the tier's quantity it would be charged.
  async function dropOrphanGifts(cart) {
    for (const gift of cart.items.filter((item) => item.properties?._gift_for)) {
      const [variantId, min] = gift.properties._gift_for.split(":");
      const bought = cart.items
        .filter((item) => String(item.variant_id) === variantId && !item.properties?._gift_for)
        .reduce((sum, item) => sum + item.quantity, 0);
      if (bought >= Number(min || 1)) continue;
      const res = await fetch(`${routesRoot}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: gift.key, quantity: 0 }),
      });
      cart = await res.json();
    }
    return cart;
  }

  function initCart() {
    if (!cartRoot || cartRoot.dataset.ddReady) return;
    cartRoot.dataset.ddReady = "1";
    const close = () => hideOverlay(cartRoot);
    $$("[data-dd-cart-close]", cartRoot).forEach((button) => button.addEventListener("click", close));
    cartRoot.addEventListener("dd:close", close);
    cartBody.addEventListener("click", (event) => {
      const button = event.target.closest("[data-dd-qty]");
      if (button) changeQuantity(button.dataset.ddQty, Number(button.dataset.qty));
    });

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-dd-cart-open]");
      if (!trigger) return;
      event.preventDefault();
      openCart();
    });

    document.addEventListener("submit", async (event) => {
      const form = event.target.closest("form[data-dd-add]");
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      button?.setAttribute("aria-busy", "true");
      try {
        let body = new FormData(form);
        const headers = { Accept: "application/json" };
        const gifts = JSON.parse(form.dataset.ddGifts || "[]");
        if (gifts.length) {
          // The tier's gifts go in with it (each once per cart), tagged with the smallest tier that gives them.
          const main = { id: Number(body.get("id")), quantity: Number(body.get("quantity") || 1) };
          if (body.get("selling_plan")) main.selling_plan = Number(body.get("selling_plan"));
          const cart = await fetchCart();
          const items = [main];
          for (const gift of gifts) {
            if (cart.items.some((item) => String(item.variant_id) === gift.id && item.properties?._gift_for)) continue;
            items.push({ id: Number(gift.id), quantity: gift.qty, properties: { _gift_for: `${main.id}:${gift.min}` } });
          }
          body = JSON.stringify({ items });
          headers["Content-Type"] = "application/json";
        }
        const res = await fetch(`${routesRoot}cart/add.js`, { method: "POST", headers, body });
        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          let message = form.querySelector("[data-dd-add-error]");
          if (!message) {
            message = document.createElement("p");
            message.dataset.ddAddError = "";
            message.className = "ddc-addError";
            message.setAttribute("role", "alert");
            form.append(message);
          }
          message.textContent = error.description || "This product could not be added to your cart.";
          return;
        }
        form.querySelector("[data-dd-add-error]")?.remove();
        form.closest("[data-dd-viewer]")?.dispatchEvent(new CustomEvent("dd:close"));
        await openCart();
      } finally {
        button?.removeAttribute("aria-busy");
      }
    });
  }

  /* Product page variant picker ---------------------------------------------- */

  function initProductForms(root = document) {
    $$("[data-dd-product]", root).forEach((section) => {
      if (section.dataset.ddReady) return;
      section.dataset.ddReady = "1";
      const product = JSON.parse($("[data-dd-product-json]", section).textContent);
      const idInput = $("[data-dd-variant-id]", section);
      const button = $("[data-dd-add-button]", section);
      const radios = $$("[data-dd-option]", section);
      if (!radios.length) return;
      const currency = window.Shopify?.currency?.active;
      section.addEventListener("change", (event) => {
        if (!event.target.matches("[data-dd-option]")) return;
        const selected = product.options.map((_, index) => $(`[data-dd-option="${index}"]:checked`, section)?.value);
        const variant = product.variants.find((entry) => entry.options.every((value, index) => value === selected[index]));
        if (!variant) return;
        idInput.value = variant.id;
        button.disabled = !variant.available;
        button.textContent = variant.available ? "Add to cart" : "Sold out";
        const priceRoot = $("[data-dd-product-price]", section);
        if (priceRoot && currency) {
          const compare = variant.compare_at_price && variant.compare_at_price > variant.price;
          const off = compare ? Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
          priceRoot.innerHTML = `<div class="ddc-priceList">
            <span class="${compare ? "ddc-salePrice" : "ddc-regularPrice"}">${formatMoney(variant.price, currency)}</span>
            ${compare ? `<span class="ddc-comparePrice">${formatMoney(variant.compare_at_price, currency)}</span><p class="ddc-badge">${off}% Off</p>` : ""}
          </div>`;
        }
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      });
    });
  }

  /* Hero reveal ---------------------------------------------------------------- */

  function initHero(root = document) {
    $$("[data-dd-hero]", root).forEach((hero) => {
      const image = $("img", hero);
      const reveal = () => hero.classList.add("ddc-heroRevealed");
      if (!image || image.complete) reveal();
      else {
        image.addEventListener("load", reveal, { once: true });
        image.addEventListener("error", reveal, { once: true });
      }
    });
  }

  /* Staggered product reveal (Impact's reveal-items) ------------------------- */

  function initReveal(root = document) {
    const lists = $$("[data-dd-reveal]", root);
    if (!("IntersectionObserver" in window)) {
      lists.forEach((list) => $$("[data-dd-reveal-item]", list).forEach((item) => item.classList.add("ddc-revealItemShown")));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          $$("[data-dd-reveal-item]", entry.target).forEach((item) => item.classList.add("ddc-revealItemShown"));
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "-50px 0px" },
    );
    lists.forEach((list) => observer.observe(list));
  }

  /* Carousel scroll progress -------------------------------------------------- */

  function initCarousels(root = document) {
    $$("[data-dd-carousel]", root).forEach((scroller) => {
      const progress = scroller.parentElement.querySelector("[data-dd-carousel-progress]");
      if (!progress) return;
      const update = () => {
        const value = (scroller.scrollLeft + scroller.clientWidth) / scroller.scrollWidth;
        progress.style.setProperty("--scroll-progress", Math.max(0, Math.min(1, value)).toFixed(6));
      };
      update();
      scroller.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update);
    });
  }

  /* Marquee timing (same formula as the Impact theme) ------------------------ */

  function initMarquees(root = document) {
    $$("[data-dd-marquee]", root).forEach((track) => {
      const speed = Number(track.dataset.speed || 10);
      new ResizeObserver(([entry]) => {
        const width = entry.contentRect.width;
        const span = track.querySelector("span");
        if (!span || !width) return;
        const slowFactor = 1 + (Math.min(1600, width) - 375) / 1225;
        track.style.setProperty("--marquee-duration", `${((speed * slowFactor * span.clientWidth) / width).toFixed(3)}s`);
      }).observe(track);
    });
  }

  /* Reels ------------------------------------------------------------------------ */

  function initReels(root = document) {
    $$("[data-dd-reels]", root).forEach((section) => {
      if (section.dataset.ddReady) return;
      section.dataset.ddReady = "1";
      const cards = $$("[data-dd-reel]", section);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;
            const existing = card.querySelector("video");
            if (entry.isIntersecting && !existing && card.dataset.preview) {
              const video = document.createElement("video");
              Object.assign(video, { src: `${card.dataset.preview}#t=0.1`, muted: true, loop: true, autoplay: true, playsInline: true });
              if (card.dataset.poster) video.poster = card.dataset.poster;
              card.insertBefore(video, card.querySelector(".ddc-reelViews"));
            } else if (!entry.isIntersecting && existing) {
              existing.remove();
            }
          });
        },
        { threshold: 0.25 },
      );
      cards.forEach((card) => observer.observe(card));

      const viewer = $("[data-dd-viewer]", section);
      if (!viewer) return;
      const video = $("[data-dd-viewer-video]", viewer);
      const prev = $("[data-dd-viewer-prev]", viewer);
      const next = $("[data-dd-viewer-next]", viewer);
      let index = 0;

      const setMuted = (muted) => {
        video.muted = muted;
        $("[data-dd-mute-label]", viewer).hidden = !muted;
        $("[data-dd-icon-muted]", viewer).hidden = !muted;
        $("[data-dd-icon-unmuted]", viewer).hidden = muted;
      };
      const show = (nextIndex) => {
        index = nextIndex;
        const card = cards[index];
        video.src = card.dataset.full || card.dataset.preview;
        if (card.dataset.poster) video.poster = card.dataset.poster;
        video.play().catch(() => {});
        $("[data-dd-viewer-views]", viewer).textContent = card.dataset.views;
        $("[data-dd-viewer-likes]", viewer).textContent = card.dataset.likes;
        prev.disabled = index === 0;
        next.disabled = index === cards.length - 1;
      };
      const close = () => {
        video.pause();
        video.removeAttribute("src");
        video.load();
        hideOverlay(viewer);
      };

      cards.forEach((card, cardIndex) =>
        card.addEventListener("click", () => {
          setMuted(true);
          show(cardIndex);
          showOverlay(viewer);
        }),
      );
      viewer.addEventListener("dd:close", close);
      viewer.addEventListener("click", (event) => {
        if (!event.target.closest("[data-dd-viewer-dialog], [data-dd-viewer-prev], [data-dd-viewer-next]")) close();
      });
      $$("[data-dd-viewer-close]", viewer).forEach((button) => button.addEventListener("click", close));
      $("[data-dd-viewer-mute]", viewer).addEventListener("click", () => setMuted(!video.muted));
      prev.addEventListener("click", () => index > 0 && show(index - 1));
      next.addEventListener("click", () => index < cards.length - 1 && show(index + 1));
      document.addEventListener("keydown", (event) => {
        if (viewer.hidden) return;
        if (event.key === "ArrowRight" && index < cards.length - 1) show(index + 1);
        if (event.key === "ArrowLeft" && index > 0) show(index - 1);
      });

      const gallery = $("[data-dd-viewer-gallery]", viewer);
      const dots = $$("[data-dd-viewer-dots] span", viewer);
      gallery?.addEventListener(
        "scroll",
        () => {
          const active = Math.round(gallery.scrollLeft / 250);
          dots.forEach((dot, dotIndex) => dot.classList.toggle("ddc-viewerDotActive", dotIndex === active));
        },
        { passive: true },
      );
    });
  }

  /* Product page: gallery, quantity breaks, panels, tooltips, video, sticky bar ---- */

  const money = (cents) => formatMoney(Math.round(cents), window.Shopify?.currency?.active || "GBP");

  function initGallery(root = document) {
    $$("[data-dd-gallery]", root).forEach((gallery) => {
      if (gallery.dataset.ddReady) return;
      gallery.dataset.ddReady = "1";
      const thumbs = $$("[data-dd-thumb]", gallery.parentElement);
      const items = $$("[data-dd-gallery-item]", gallery);
      thumbs.forEach((thumb) =>
        thumb.addEventListener("click", () => {
          const item = items[Number(thumb.dataset.ddThumb)];
          gallery.scrollTo({ left: item.offsetLeft - gallery.offsetLeft, behavior: "smooth" });
        }),
      );
      gallery.addEventListener(
        "scroll",
        () => {
          const index = Math.round(gallery.scrollLeft / gallery.clientWidth);
          thumbs.forEach((thumb, i) => thumb.setAttribute("aria-current", String(i === index)));
        },
        { passive: true },
      );
    });
  }

  function initBundles(root = document) {
    $$("[data-dd-bundle]", root).forEach((bundle) => {
      if (bundle.dataset.ddReady) return;
      bundle.dataset.ddReady = "1";
      const form = document.getElementById(bundle.dataset.form);
      const qtyInput = form?.querySelector("[data-dd-quantity]");
      const idInput = form?.querySelector("[data-dd-variant-id]");
      const planInput = form?.querySelector("[data-dd-selling-plan]");
      const total = form?.querySelector("[data-dd-atc-total]");
      const savings = $("[data-dd-savings]", bundle);
      // The reference's A/B test: a number from 0 to 100 kept per visitor picks the variant, as the bundle app does.
      const groups = $$("[data-dd-ab]", bundle);
      if (groups.length) {
        const key = `ddc-ab-${bundle.dataset.form}`;
        let roll = NaN;
        try {
          const stored = localStorage.getItem(key);
          if (stored !== null) roll = Number(stored);
        } catch (error) {}
        if (!(roll >= 0 && roll < 100)) {
          roll = Math.random() * 100;
          try { localStorage.setItem(key, String(roll)); } catch (error) {}
        }
        const chosen = groups.find((group) => {
          const [from, to] = group.dataset.ddAbRange.split(",").map(Number);
          return roll >= from && roll < to;
        }) || groups[0];
        groups.forEach((group) => (group === chosen ? group.removeAttribute("hidden") : group.remove()));
      }
      const bars = $$("[data-dd-bar]", bundle);
      const perItem = bundle.dataset.perItem === "true";
      const useCompare = bundle.dataset.useCompare === "true";
      const planPercent = Number(bundle.dataset.planPercent || 0);
      let price = Number(bundle.dataset.price);
      let compare = Number(bundle.dataset.compare || 0);
      let subscribed = bundle.dataset.subscribed === "true";

      const priceOf = (bar) => (bar.dataset.price ? Number(bar.dataset.price) : price);
      const compareOf = (bar) => (bar.dataset.price ? Number(bar.dataset.compare || 0) : compare);
      const unitFor = (bar) => {
        const base = priceOf(bar);
        const qty = Number(bar.dataset.qty);
        const value = Number(bar.dataset.discountValue || 0);
        let unit = base;
        // Shopify takes a percentage off each unit in whole cents, rounding the discount down.
        if (bar.dataset.discountType === "percentage") unit = base - Math.floor((base * value) / 100 + 1e-9);
        if (bar.dataset.discountType === "fixed" || bar.dataset.discountType === "amount") unit = Math.max(0, base - value * 100);
        if (bar.dataset.discountType === "specific") unit = (value * 100) / qty;
        if (bar.dataset.discountType === "specific" && !perItem && !subscribed) return (value * 100) / qty;
        if (subscribed) unit = Math.round(unit * (1 - planPercent / 100));
        return Math.round(unit);
      };
      // A "specific" tier sets the bundle's total price (a fixed-amount discount in the cart): the total is
      // exact even when the per-unit figure is rounded for display.
      const totalFor = (bar) =>
        bar.dataset.discountType === "specific" && !subscribed
          ? Math.round(Number(bar.dataset.discountValue) * 100)
          : unitFor(bar) * Number(bar.dataset.qty);
      // A gift's own price counts in the struck total and in the savings, as on the reference.
      const giftsOf = (bar) => $$("[data-dd-gift]", bar).map((gift) => ({ id: gift.dataset.id, qty: Number(gift.dataset.qty || 1), value: Number(gift.dataset.value || 0), worth: Number(gift.dataset.worth || 0), min: Number(gift.dataset.min || bar.dataset.qty) }));
      const giftValueOf = (bar) => giftsOf(bar).reduce((sum, gift) => sum + gift.value, 0);
      const giftWorthOf = (bar) => giftsOf(bar).reduce((sum, gift) => sum + gift.worth, 0);
      const fullFor = (bar) => {
        const barCompare = compareOf(bar);
        return useCompare && barCompare > priceOf(bar) ? barCompare : priceOf(bar);
      };

      const render = () => {
        bars.forEach((bar) => {
          const qty = Number(bar.dataset.qty);
          const unit = unitFor(bar);
          const fullEach = fullFor(bar);
          const barTotal = totalFor(bar);
          $("[data-dd-bar-price]", bar).textContent = money(perItem ? unit : barTotal);
          const full = $("[data-dd-bar-full]", bar);
          const fullTotal = fullEach * qty + giftValueOf(bar);
          full.textContent = perItem ? (fullEach > unit ? money(fullEach) : "") : fullTotal > barTotal ? money(fullTotal) : "";
          const label = $("[data-dd-bar-label]", bar);
          const percent = fullEach > unit ? Math.round(((fullEach - unit) * 100) / fullEach) : 0;
          if (bar.dataset.label && percent > 0) {
            label.textContent = bar.dataset.label.replace("{{saved_percentage}}", `${percent}%`).replace("{{saved_total}}", money(fullEach * qty - totalFor(bar)));
            label.hidden = false;
          } else {
            label.hidden = true;
          }
        });
        const selected = bars.find((bar) => $("input", bar).checked) || bars[0];
        if (!selected) return;
        const qty = Number(selected.dataset.qty);
        const unit = unitFor(selected);
        const fullEach = fullFor(selected);
        if (qtyInput) qtyInput.value = qty;
        if (form) form.dataset.ddGifts = JSON.stringify(giftsOf(selected));
        if (idInput && selected.dataset.variantId) idInput.value = selected.dataset.variantId;
        else if (idInput && bundle.dataset.defaultVariant) idInput.value = bundle.dataset.defaultVariant;
        if (planInput) {
          planInput.disabled = !subscribed || !bundle.dataset.planId;
          planInput.value = subscribed ? bundle.dataset.planId : "";
        }
        if (total && bundle.dataset.totalInButton === "true") {
          total.textContent = `· ${money(totalFor(selected))}`;
          total.hidden = false;
        }
        if (savings) {
          const saved = fullEach * qty + giftWorthOf(selected) - totalFor(selected);
          savings.hidden = saved <= 0;
          const [before, after = ""] = (bundle.dataset.savingsTitle || "You're saving {{saved_total}} on this order").split("{{saved_total}}");
          $("[data-dd-savings-text]", savings).innerHTML = `${escapeHtml(before)}<strong>${money(saved)}</strong>${escapeHtml(after)}`;
        }
      };

      bars.forEach((bar) => $("input", bar).addEventListener("change", render));
      $$("[data-dd-plan]", bundle).forEach((button) =>
        button.addEventListener("click", () => {
          subscribed = button.dataset.ddPlan === "subscribe";
          $$("[data-dd-plan]", bundle).forEach((other) => other.setAttribute("aria-pressed", String(other === button)));
          render();
        }),
      );
      $$("[data-dd-bundle-variant]", bundle).forEach((button) =>
        button.addEventListener("click", () => {
          price = Number(button.dataset.price);
          compare = Number(button.dataset.compare || 0);
          if (idInput) idInput.value = button.dataset.ddBundleVariant;
          $$("[data-dd-bundle-variant]", bundle).forEach((other) => other.setAttribute("aria-pressed", String(other === button)));
          render();
        }),
      );
      render();
    });
  }

  function initPanels(root = document) {
    $$("[data-dd-panel-open]", root).forEach((trigger) => {
      if (trigger.dataset.ddReady) return;
      trigger.dataset.ddReady = "1";
      const modal = document.querySelector(`[data-dd-panel="${trigger.dataset.ddPanelOpen}"]`);
      if (!modal) return;
      const close = () => hideOverlay(modal);
      trigger.addEventListener("click", () => showOverlay(modal));
      trigger.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        showOverlay(modal);
      });
      modal.addEventListener("dd:close", close);
      modal.addEventListener("click", (event) => {
        if (event.target === modal || event.target.closest("[data-dd-panel-close]")) close();
      });
    });
  }

  function initTooltips(root = document) {
    $$("[data-dd-tip-toggle]", root).forEach((button) => {
      if (button.dataset.ddReady) return;
      button.dataset.ddReady = "1";
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        const tip = button.closest("[data-dd-tip]");
        const open = !tip.classList.contains("is-open");
        $$("[data-dd-tip].is-open").forEach((other) => other.classList.remove("is-open"));
        tip.classList.toggle("is-open", open);
      });
    });
    $$("[data-dd-scroll-top]", root).forEach((button) =>
      button.addEventListener("click", () => {
        const target = $("[data-dd-bundle]") || $("[data-dd-product]") || document.body;
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }),
    );
  }
  document.addEventListener("click", () => $$("[data-dd-tip].is-open").forEach((tip) => tip.classList.remove("is-open")));

  function initVideos(root = document) {
    $$("[data-dd-video]", root).forEach((frame) => {
      const play = $("[data-dd-video-play]", frame);
      const video = $("[data-dd-video-player]", frame);
      if (!play || !video || play.dataset.ddReady) return;
      play.dataset.ddReady = "1";
      play.addEventListener("click", () => {
        play.hidden = true;
        video.play().catch(() => {});
      });
    });
  }

  function initStickyAtc(root = document) {
    $$("[data-dd-sticky-atc]", root).forEach((bar) => {
      if (bar.dataset.ddReady) return;
      bar.dataset.ddReady = "1";
      const section = bar.closest("[data-dd-product]");
      const anchor = $("[data-dd-add-button]", section) || $("[data-dd-bundle]", section);
      if (!anchor) return;
      new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(anchor);
      $("[data-dd-sticky-target]", bar).addEventListener("click", () => {
        ($("[data-dd-bundle]", section) || anchor).scrollIntoView({ behavior: "smooth", block: "center" });
      });
    });
  }

  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-dd-filter-submit]")) event.target.form.submit();
  });

  /* Trustpilot reviews (native) ---------------------------------------------- */

  const AGO = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];

  function timeAgo(date) {
    const seconds = (Date.now() - date.getTime()) / 1000;
    for (const [unit, size] of AGO) {
      const count = Math.floor(seconds / size);
      if (count >= 1) return `${count} ${unit}${count > 1 ? "s" : ""} ago`;
    }
    return "just now";
  }

  function initTrustpilot(root = document) {
    root.querySelectorAll("[data-dd-ago]").forEach((time) => {
      const date = new Date(time.getAttribute("datetime"));
      if (!Number.isNaN(date.getTime())) time.textContent = timeAgo(date);
    });
  }

  document.addEventListener("click", (event) => {
    const more = event.target.closest("[data-dd-tp-more]");
    if (!more) return;
    const card = more.closest("[data-dd-tp-card]");
    const open = card.classList.toggle("is-open");
    more.dataset.more ??= more.textContent;
    more.textContent = open ? more.dataset.less : more.dataset.more;
  });

  /* FrontrowMD clinician widgets (native) --------------------------------------- */

  const frModal = (name) => document.querySelector(`[data-dd-fr-modal="${name}"]`);

  function openFrModal(name) {
    const modal = frModal(name);
    if (!modal) return;
    modal.hidden = false;
    document.documentElement.classList.add("ddc-frLocked");
  }

  function closeFrModal(name) {
    const modals = name ? [frModal(name)] : document.querySelectorAll("[data-dd-fr-modal]");
    modals.forEach((modal) => modal && (modal.hidden = true));
    if (!document.querySelector("[data-dd-fr-modal]:not([hidden])")) {
      document.documentElement.classList.remove("ddc-frLocked");
    }
  }

  function openProvider(button) {
    const modal = frModal("provider");
    if (!modal) return;
    const data = button.dataset;
    const name = data.name?.trim() || button.textContent.trim();
    const images = modal.querySelector(".images");
    images.replaceChildren();
    images.className = "images mural-1";
    (data.images || "").split(",").filter(Boolean).forEach((src) => {
      const img = document.createElement("img");
      img.src = src.trim();
      img.alt = `Portrait of ${name}`;
      images.append(img);
    });
    modal.querySelector(".fr-doctor-title").textContent = name;
    const fields = {
      specialty: data.specialty,
      location: data.location,
      npinumber: data.npinumber,
      practicetype: data.practicetype,
      joinedfrontrow: data.joinedfrontrow,
      education: data.education,
      yearsinpractice: data.yearsinpractice,
    };
    Object.entries(fields).forEach(([key, value]) => {
      const cell = modal.querySelector(`dd.${key}`);
      if (!cell) return;
      cell.textContent = value || "";
      cell.parentElement.hidden = !value;
    });
    openFrModal("provider");
  }

  // The reference keeps the badge at its full 437px on every screen; on a narrow one the edge cuts it.
  function fitFrSticker(sticker) {
    sticker.style.removeProperty("transform");
  }

  function initFrontrow(root = document) {
    root.querySelectorAll("[data-dd-fr-sticker]").forEach((sticker) => {
      fitFrSticker(sticker);
      if (!sticker.ddFit) {
        sticker.ddFit = true;
        window.addEventListener("resize", () => fitFrSticker(sticker));
      }
    });
  }

  document.addEventListener("click", (event) => {
    const pill = event.target.closest(".cp-citation-pill");
    document.querySelectorAll(".cp-citation-tooltip.active").forEach((tooltip) => {
      if (pill && tooltip.parentElement === pill.parentElement) return;
      tooltip.classList.remove("active");
      tooltip.parentElement.querySelector(".cp-citation-pill")?.classList.remove("active");
    });
    if (pill) {
      const tooltip = pill.parentElement.querySelector(".cp-citation-tooltip");
      pill.classList.toggle("active", tooltip.classList.toggle("active"));
      return;
    }

    const trigger = event.target.closest("[data-dd-fr]");
    if (!trigger) return;
    const action = trigger.dataset.ddFr;
    if (action === "screen" && event.target !== trigger) return;
    event.preventDefault();
    if (action === "modal") openFrModal("info");
    if (action === "provider") openProvider(trigger);
    if (action === "close" || action === "screen") closeFrModal(action === "screen" ? trigger.closest("[data-dd-fr-modal]").dataset.ddFrModal : null);
    if (action === "back") closeFrModal(trigger.closest("[data-dd-fr-modal]").dataset.ddFrModal);
    if (action === "more") {
      const message = trigger.closest(".note--content").querySelector(".note--message");
      const open = message.classList.toggle("note--line-clamp") === false;
      trigger.textContent = open ? "Show less" : "Show more";
    }
    if (action === "toggle") {
      const sticker = trigger.closest("[data-dd-fr-sticker]");
      const expand = sticker.dataset.state === "min";
      if (expand) sticker.style.removeProperty("width");
      else sticker.style.width = "40px";
      setTimeout(() => (sticker.dataset.state = expand ? "max" : "min"), 300);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeFrModal();
  });

  /* Boot ----------------------------------------------------------------------- */

  function init(root = document) {
    initGallery(root);
    initBundles(root);
    initPanels(root);
    initTooltips(root);
    initVideos(root);
    initStickyAtc(root);
    initMenu(root);
    initFilters(root);
    initPriceRanges(root);
    initAmazonReviews(root);
    initCountrySelectors(root);
    initProductForms(root);
    initHero(root);
    initReveal(root);
    initCarousels(root);
    initMarquees(root);
    initReels(root);
    initTrustpilot(root);
    initFrontrow(root);
  }

  initCart();
  init();
  // Re-bind when sections are edited in the theme editor.
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
